import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableItem } from "./components/Item";

interface SortableListItem {
  id: string | number;
  content: React.ReactNode;
}

interface SingleSortableListProps {
  items: SortableListItem[];
  onReorder: (newItems: SortableListItem[]) => void;
  style?: React.CSSProperties;
  handle?: boolean;
}

export const SingleSortableList = ({
  items,
  onReorder,
  style,
  handle = false,
}: SingleSortableListProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over?.id);

      const newItems = arrayMove(items, oldIndex, newIndex);
      onReorder(newItems);
    }
  };

  const getIndex = (id: string | number): number => {
    return items.findIndex((item) => item.id === id);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        ...style,
      }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          {items.map((item, index) => (
            <SortableItem
              key={item.id}
              id={item.id}
              value={item.content}
              index={index}
              containerId="single-list"
              getIndex={getIndex}
              handle={handle}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
};
